import { makeAutoObservable, runInAction, action } from "mobx";
import { authService } from "../services/AuthService";
import type { LoginCredentials, UserResponse } from "@/types/User.interface";
import { userStore } from "./UserStore";
import { permissionsStore } from "./PermissionsStore";
import { addNotification } from "@/utils/addNotification";

export interface UserData {
  id?: number;
  username?: string;
  role?: number;
  fio?: string;
  lastLogin?: string | null;
}

export class AuthStore {
  isLoading = false;
  isLoadingAuth: boolean = false;
  error: string | null = null;
  //eslint-disable-next-line
  errorDetails: any = null;
  isLoadingImage = true;
  isImage: string = "";
  isPhraseArray: string[] = [];
  private loginTimerId: NodeJS.Timeout | null = null;
  neededLogin: boolean = true;
  first: boolean = true;

  constructor() {
    makeAutoObservable(this);
  }

  // Метод для очистки таймера
  private clearLoginTimer() {
    if (this.loginTimerId) {
      clearTimeout(this.loginTimerId);
      this.loginTimerId = null;
    }
  }

  dispose() {
    this.clearLoginTimer();
  }

  getImage = async () => {
    runInAction(() => {
      this.isLoadingImage = true;
      this.isPhraseArray = [];
    });

    try {
      const startTime = Date.now();
      const result = await authService.getImage();

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 2000 - elapsedTime);

      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      runInAction(() => {
        if (result.data && result.data.captcha_id) {
          this.isImage = result.data.image_data || "";
          this.isPhraseArray = result.data.phrase_array || [];

          // Сохраняем captcha_id в AuthLocalData
          authLocalData.setCaptchaId(result.data.captcha_id);
        } else {
          // Если нет captcha_id, очищаем
          authLocalData.clearCaptchaId();
        }
        this.isLoadingImage = false;
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
    }
  };

  async checkAuth() {
    const apiToken = localStorage.getItem("api_token");

    if (!apiToken) {
      runInAction(() => {
        this.neededLogin = true;
        this.isLoading = false;
        this.first = true;
      });
      return { success: false, message: "Отсутствует токен авторизации" };
    }

    runInAction(() => {
      if (this.first) {
        this.isLoading = true;
        this.error = null;
      }
    });

    try {
      const result = await authService.checkAuth();

      runInAction(() => {
        if (this.first) {
          this.isLoading = false;
        }
      });

      if (result.success) {
        // Загружаем данные пользователя после успешной проверки авторизации
        await userStore.setUser();
        if (result.other_users) {
          userStore.setUserFromData(result.other_users);
        }
        if (this.first) {
          // Загружаем данные прав после успешной проверки авторизации
          await permissionsStore.setPermissions();
          runInAction(() => {
            this.first = false;
          });
        }
        runInAction(() => {
          // Успешная проверка авторизации
          this.error = null;
          this.neededLogin = false;
        });
      } else {
        runInAction(() => {
          // Ошибка авторизации
          this.error = result.message || "Ошибка проверки авторизации";
          this.neededLogin = true;
        });
        if (result.message !== "Отсутствует токен авторизации") {
          userStore.clearUser();
        }
      }

      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
      runInAction(() => {
        this.isLoading = false;
        this.error = "Ошибка сервера при проверке авторизации";
        this.neededLogin = true;
        userStore.clearUser();
      });
      return { success: false, error: message };
    }
  }

  setFirst() {
    this.first = true;
  }

  // Новый метод для установки авторизованного пользователя
  setAuthenticatedUser = (userData: UserResponse) => {
    runInAction(() => {
      // Устанавливаем состояние авторизации
      this.neededLogin = false;
      this.error = null;
      this.isLoading = false;

      // Преобразуем и устанавливаем пользователя в UserStore
      userStore.setUserFromData({
        id: userData.id,
        login: userData.login,
        role: userData.role,
        fio: userData.fio,
        active: userData.active ?? true,
        lastLogin: userData.lastLogin ?? null,
        other_users: userData.otherUsers ?? [],
      });
    });
  };

  login = async (credentials: LoginCredentials & { captcha_id?: string }) => {
    // Добавьте captcha_id из локального хранилища
    const fullCredentials = {
      ...credentials,
      captcha_id: authLocalData.captchaId, // Нужно добавить это поле в AuthLocalData
    };

    this.clearLoginTimer();
    authLocalData.setValuePhrase([]);

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
      this.errorDetails = null;
    });

    try {
      const result = await authService.login(fullCredentials);

      runInAction(() => {
        this.isPhraseArray = [];
        this.isLoading = false;
      });
      // Устанавливаем пользователя при успешном логине
      if (result.success && result.data && result.data.user) {
        if (this.first) {
          await permissionsStore.setPermissions();
          await this.checkAuth();
          runInAction(() => {
            this.first = false;
          });
        }
        this.setAuthenticatedUser({
          id: result.data.user.id,
          login: result.data.user.login,
          role: result.data.user.role,
          fio: result.data.user.fio || "",
          active: result.data.user.active || true,
          lastLogin: result.data.user.lastLogin || null,
          otherUsers: result.data.user.otherUsers || [],
        });

        runInAction(() => {
          this.error = null;
        });
      } else {
        userStore.clearUser();
        runInAction(() => {
          this.error = result.error || "Ошибка авторизации";
          this.neededLogin = true;
        });
        addNotification({ message: this.error, type: "error" });
      }

      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Неизвестная ошибка";
      addNotification({ message: message, type: "error" });
      runInAction(() => {
        this.isLoadingAuth = false;
        userStore.clearUser();
        this.error = message;
        this.neededLogin = true;
      });
      return { success: false, error: message };
    }
  };

  addNewLocalError = (error: string) => {
    runInAction(() => {
      this.error = error;
    });
  };

  clearUserData() {
    runInAction(() => {
      userStore.clearUser();
      this.isPhraseArray = [];
      this.error = null;
      this.neededLogin = true;
      authLocalData.setValuePhrase([]);
    });
  }

  async logout() {
    runInAction(() => {
      this.first = true;
      this.neededLogin = true;
      this.isLoading = true;
      userStore.clearUser();
    });

    try {
      const success = await authService.logout();

      if (success) {
        this.clearUserData();
      } else {
        runInAction(() => {
          this.error = "Отсутствует авторизация или вышло время сессии";
          this.isLoading = false;
        });
      }

      return success;
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
      runInAction(() => {
        this.error = message;
      });
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  clearError() {
    runInAction(() => {
      this.error = null;
      this.errorDetails = null;
    });
  }

  get hasError() {
    return !!this.error;
  }

  get neededCaptcha() {
    return this.neededLogin;
  }
}

export const authStore = new AuthStore();

class AuthLocalData {
  valuePhrase: string[] = [];
  pazzleParent: HTMLDivElement | null = null;
  pazzleParentClassName: string = "";
  error: string | null = null;
  captchaId: string = "";

  constructor() {
    makeAutoObservable(this);
  }

  // Добавляем метод для установки captcha_id
  setCaptchaId = action((id: string) => {
    runInAction(() => {
      this.captchaId = id;
    });
  });

  // Добавляем метод для очистки captcha_id
  clearCaptchaId = action(() => {
    runInAction(() => {
      this.captchaId = "";
    });
  });

  setValuePhrase = action((valuePhrase: string | string[]) => {
    runInAction(() => {
      if (valuePhrase instanceof Array) {
        this.valuePhrase = valuePhrase;
      } else {
        this.valuePhrase = [...this.valuePhrase, valuePhrase];
      }
    });
  });

  setNodePuzzle = (node: HTMLDivElement, className: string) => {
    runInAction(() => {
      this.pazzleParent = node;
      this.pazzleParentClassName = className;
    });
  };

  clearFields = () => {
    if (this.pazzleParent) {
      this.pazzleParent.querySelectorAll("div").forEach((puzzle) => {
        if (puzzle.classList.contains(this.pazzleParentClassName)) {
          puzzle.classList.remove(this.pazzleParentClassName);
        }
      });
    }

    runInAction(() => {
      this.setValuePhrase([]);
    });
  };

  addNewLocalError = (error: string) => {
    runInAction(() => {
      this.error = error;
    });
    authStore.addNewLocalError(error);
  };

  clearError() {
    runInAction(() => {
      this.error = null;
    });
  }
}

export const authLocalData = new AuthLocalData();
